import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUrlString } from '../../hooks/useUrlState';
import { Card, Typography, Tag, Tooltip, Tabs, EmptyState, Spinner, Button, Modal } from '../../ui';
import { usePermission } from '../../store/auth';
import { RecordDepositModal } from './RecordDepositModal';
import { Wallet, HandCoins, Info } from 'lucide-react';
import { request } from '../../api/client';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { OrderLink } from '../../components/EntityLinks';
import { formatIst } from '../../lib/time';
import { inr2 } from '../../lib/format';

/**
 * MY MONEY — two books, side by side, never summed.
 *
 * A field rep can be holding company money in two completely different senses at once, and the whole
 * point of this screen is that they stay apart:
 *
 *   WALLET   — float the company released to them TO SPEND on approved business expenses.
 *   CUSTODY  — physical cash they collected from retailers and must RETURN to the company.
 *
 * Adding them produces a number that is wrong in both directions: it inflates what the rep may spend
 * and hides what they owe. So there is deliberately no total, no "available balance", and no combined
 * figure anywhere on this page — only two balances and one chronological ledger with a column each.
 *
 * The running balances are computed INDEPENDENTLY down the same list: a wallet row moves only the
 * wallet column, a custody row moves only the custody column, and the other side shows "—".
 */

type Book = 'WALLET' | 'RETAILER_MONEY';

const CUSTODY_LABEL: Record<string, string> = {
  COLLECTED: 'Collected from a retailer',
  DEPOSITED: 'Deposited to a company account',
  SPENT_ON_EXPENSE: 'Used on an approved expense claim',
  MOVED_TO_WALLET: 'Moved into your wallet for an advance',
};

const WALLET_LABEL: Record<string, string> = {
  ADVANCE_IN: 'Advance received',
  REIMBURSEMENT: 'Reimbursed',
  EXPENSE_REVERSAL: 'Claim reversed',
  REP_CASH_IN: 'Added to float',
  EXPENSE_OUT: 'Expense claimed',
  REP_CASH_OUT: 'Spent from float',
  RETURN: 'Float returned',
  ADJUSTMENT: 'Adjustment',
  COLLECTION_TO_FLOAT: 'From your collection cash',
};

export function MyMoneyLedger({ only }: { only?: Book } = {}) {
  /*
   * `only` pins this to a single book and hides the internal tab bar, so the component can be
   * mounted AS a page tab instead of carrying tabs of its own. Without it the rep's page would show
   * a Wallet tab inside a My-money tab beside a My-wallet tab — the same balance three levels deep.
   */
  const [book, setBook] = useUrlString<Book>('book', only ?? 'WALLET');
  const [depositOpen, setDepositOpen] = useState(false);
  /* The deposit row whose full dealer breakdown the rep asked to see. */
  const [coversFor, setCoversFor] = useState<any | null>(null);
  const canDeposit = usePermission('CASH.DEPOSIT.RECORD');
  const { data, isLoading } = useQuery({
    queryKey: ['my-money-ledger'],
    queryFn: () => request<any>({ url: '/retailer-money/my-ledger' }),
  });

  const walletBal = Number(data?.wallet?.balance ?? 0);
  const custodyBal = Number(data?.retailerMoney?.outstanding ?? 0);
  const collectedTotal = Number(data?.retailerMoney?.collected ?? 0);
  const depositedTotal = Number(data?.retailerMoney?.deposited ?? 0);
  const spentOnExpenses = Number(data?.retailerMoney?.spentOnExpenses ?? 0);
  /* Collected cash moved into the wallet for an approved advance — it left this book, but it was not spent. */
  const movedToWallet = Number(data?.retailerMoney?.movedToWallet ?? 0);
  /* Banked, but nobody has confirmed it against the account yet. Inside `custodyBal`, not subtracted
     from it — shown separately so a rep who has just recorded a deposit can see that it registered. */
  const awaitingConfirmation = Number(data?.retailerMoney?.awaitingConfirmation ?? 0);
  const allRows: any[] = data?.rows ?? [];

  /*
   * TWO running balances, computed over the FULL history in date order and only then filtered — so
   * switching the filter never rewrites a balance. Each row carries the balance of its own book as of
   * that moment, and a dash for the other one.
   */
  const rows = useMemo(() => {
    /*
     * Each book starts from the balance implied by its OWN earliest visible row, not from zero. The
     * server returns a window of recent history, so a rep with a long ledger would otherwise see a
     * running balance that restarts at zero mid-life and disagrees with both the header figure and
     * the balance stamped on the row when it was posted.
     */
    const openingOf = (b: Book) => {
      const first = allRows.find((r) => r.book === b);
      if (!first || first.balanceAfter == null) return 0;
      const signed = first.direction === 'IN' ? first.amount : -first.amount;
      return Math.round((Number(first.balanceAfter) - signed) * 100) / 100;
    };
    let w = openingOf('WALLET');
    let c = openingOf('RETAILER_MONEY');
    const withRunning = allRows.map((r) => {
      const signed = r.direction === 'IN' ? r.amount : -r.amount;
      /* A deposit nobody has checked yet, or one the bank could not show, is on the list but not in the
         arithmetic — the money is still this person's responsibility either way, so the balance holds
         still and the row says which. Matching the header figure, which excludes both for the same
         reason. */
      if (!r.pendingVerification && !r.rejected) {
        if (r.book === 'WALLET') w = Math.round((w + signed) * 100) / 100;
        else c = Math.round((c + signed) * 100) / 100;
      }
      return { ...r, walletRunning: r.book === 'WALLET' ? w : null, custodyRunning: r.book === 'RETAILER_MONEY' ? c : null, _key: `${r.book}-${r.id}` };
    });
    const filtered = withRunning.filter((r) => r.book === book);
    return filtered.slice().reverse();   // newest first on screen, arithmetic stays chronological
  }, [allRows, book]);

  const cols: DataTableColumn<any>[] = [
    {
      key: 'at', title: 'Date', width: 130, sortable: true,
      render: (_: any, r: any) => (
        <span className="block leading-tight">
          <span className="block whitespace-nowrap text-content">{formatIst(r.at, 'DD MMM YYYY')}</span>
          <span className="block whitespace-nowrap type-caption text-subtle">{formatIst(r.at, 'hh:mm A')}</span>
        </span>
      ),
    },
    {
      key: 'doc', title: 'Document', width: 175,
      render: (_: any, r: any) => (
        <span className="block min-w-0 leading-tight">
          <span className="block truncate font-mono text-xs text-content" title={r.docNo ?? ''}>{r.docNo ?? '—'}</span>
          {/* Every row leads back to what caused it. */}
          <span className="block truncate type-caption text-subtle">
            {r.source?.orderNo
              ? <OrderLink id={r.source.orderId}>{r.source.orderNo}</OrderLink>
              : r.source?.depositNo ?? r.source?.transferNo ?? r.source?.paymentNo ?? ''}
          </span>
        </span>
      ),
    },
    {
      key: 'desc', title: 'Description', width: 460,
      render: (_: any, r: any) => (
        <span className="block min-w-0 leading-tight">
          <span className="flex min-w-0 items-center gap-1.5">
            {/* Only when both books share the list. Pinned to one, the tab above already says which
                book this is, and a "Retailer" chip repeated down every row is pure furniture. */}
            {!only && (
              <Tag tone={r.book === 'WALLET' ? 'blue' : 'gold'} style={{ margin: 0 }} className="shrink-0">
                {r.book === 'WALLET' ? 'Wallet' : 'Retailer'}
              </Tag>
            )}
            <span className="truncate text-content">
              {r.book === 'WALLET' ? (WALLET_LABEL[r.type] ?? r.type) : (CUSTODY_LABEL[r.type] ?? r.type)}
            </span>
            {/* Why this row left the balance where it was. Without it the deposit looks like it failed —
                or, for a rejected one, never happened at all. */}
            {r.pendingVerification && (
              <Tag tone="gold" style={{ margin: 0 }} className="shrink-0">Awaiting confirmation</Tag>
            )}
            {r.rejected && (
              <Tag tone="red" style={{ margin: 0 }} className="shrink-0">Not found in the bank</Tag>
            )}
          </span>
          {r.rejected && (
            <span className="mt-1 block rounded-control border border-danger-500/30 bg-danger-500/5 px-2 py-1 type-caption text-danger-600 dark:text-danger-500">
              <b>Still yours to account for.</b>{r.verificationRemark ? ` ${r.verificationRemark}` : ' Accounts could not find this deposit in the account.'}
            </span>
          )}
          {(r.description || r.source?.retailerName) && (
            <span className="block truncate type-caption text-subtle" title={r.description ?? ''}>
              {r.source?.retailerName ? `${r.source.retailerName} · ` : ''}{r.description}
            </span>
          )}
          {/*
            * WHOSE money this deposit was.
            *
            * "Deposited ₹20,000 into HDFC" answers half the question and leaves the more useful half
            * — which collections did that clear? — to be worked out from a bank reference. The
            * allocation already knows, so it says so: the retailers, oldest first, with the slice of
            * each that this deposit covered.
            */}
          {r.source?.covers?.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setCoversFor(r); }}
              className="mt-0.5 block max-w-full truncate text-left type-caption text-subtle hover:text-brand-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:hover:text-brand-400"
            >
              covers{' '}
              {r.source.covers.slice(0, 2).map((c: any, i: number) => (
                <span key={i}>
                  {i > 0 ? ', ' : ''}
                  <span className="text-content">{c.retailerName ?? 'Unknown retailer'}</span>
                  {' '}{inr2(c.amount)}
                </span>
              ))}
              {/* A day's round can be a dozen dealers. The rest were only ever in a hover title —
                  unreachable on a phone, and not something you can read down or copy. */}
              {r.source.covers.length > 2 && (
                <span className="text-brand-700 dark:text-brand-400"> +{r.source.covers.length - 2} more</span>
              )}
            </button>
          )}
        </span>
      ),
    },
    {
      /*
       * ONE money column, because the open tab already says which book this is.
       *
       * There used to be two, each printing a dash on every row that belonged to the other one — half
       * the money area saying "not this". Worse, the two running balances sat adjacent, which is the
       * one arrangement that invites adding them together.
       */
      key: 'amount',
      title: book === 'WALLET' ? 'Wallet' : 'Collection money',
      align: 'right',
      width: 190,
      render: (_: any, r: any) => {
        /*
         * A ROW THAT DID NOT MOVE THE BALANCE MUST NOT LOOK LIKE ONE THAT DID.
         *
         * Printed in the same red as a real deposit, a pending or rejected claim produced two
         * consecutive −₹10,000 rows both ending at ₹33,150 — arithmetic that reads as broken. It is
         * not broken: neither claim has reduced anything, and the ledger is right to hold still. So
         * the figure is struck through and muted, and the line underneath says the balance is
         * unchanged instead of repeating a number that never moved.
         */
        const inert = r.pendingVerification || r.rejected;
        const bal = (book === 'WALLET' ? r.walletRunning : r.custodyRunning) ?? 0;
        return (
          <span className="block leading-tight">
            <span className={`block whitespace-nowrap tabular-nums ${
              inert
                ? 'text-subtle line-through decoration-1'
                : r.direction === 'IN' ? 'text-success-600 dark:text-success-500' : 'text-danger-600 dark:text-danger-500'
            }`}>
              {r.direction === 'IN' ? '+' : '−'}{inr2(r.amount)}
            </span>
            <span className="block whitespace-nowrap type-caption text-subtle">
              {inert ? `balance unchanged · ${inr2(bal)}` : `balance ${inr2(bal)}`}
            </span>
          </span>
        );
      },
    },
  ];

  if (isLoading) {
    return <Card className="mt-4"><div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div></Card>;
  }

  return (
    <Card className="mt-4">
      {/* Pinned to one book, the card is already sitting under a tab of the same name — repeating the
          title and a two-books caption would be saying it three times. */}
      {!only && (
        <div className="mb-3">
          <Typography.Title level={5} style={{ margin: 0 }}>My money</Typography.Title>
          <div className="type-caption text-muted">
            Two separate books. One is company money you may spend; the other is cash you collected that
            the company is waiting on. They are never added together.
          </div>
        </div>
      )}

      {/*
        * ONE TAB PER BOOK.
        *
        * Wallet is company float this person may SPEND. Collection money is dealer cash the company is
        * WAITING ON. Putting them in one table meant every row carried a dash in the column it did not
        * belong to, and the two running balances sat side by side inviting exactly the addition the
        * whole design exists to prevent.
        *
        * A tab each gives every row a single, unambiguous meaning — and the balances stay in the tab
        * LABELS, so both numbers are still on screen at once. You can see them together; you just
        * cannot read them as one account.
        */}
      {!only && (
      <Tabs
        activeKey={book}
        onChange={(k) => setBook(k as Book)}
        items={[
          {
            key: 'WALLET',
            label: (
              <span className="flex items-center gap-2">
                <Wallet size={14} className="shrink-0 text-brand-600 dark:text-brand-400" />
                <span>Wallet</span>
                <b className="tabular-nums text-content">{inr2(walletBal)}</b>
              </span>
            ),
          },
          {
            key: 'RETAILER_MONEY',
            label: (
              <span className="flex items-center gap-2">
                <HandCoins size={14} className="shrink-0 text-warning-600 dark:text-harvest-300" />
                <span>Collection money</span>
                <b className={`tabular-nums ${custodyBal > 0.005 ? 'text-warning-600 dark:text-harvest-300' : 'text-content'}`}>
                  {inr2(custodyBal)}
                </b>
              </span>
            ),
          },
        ]}
      />
      )}

      {/* The open book, stated once: what the figure is, what it means, and what can be done about it. */}
      <div className="mt-4 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="type-overline text-subtle">
              {book === 'WALLET' ? 'Wallet · company float' : 'Collection money outstanding'}
            </span>
            <Tooltip label={book === 'WALLET'
              ? 'Company money released to you as float or advance, to spend on approved business expenses.'
              : 'Cash you collected from retailers that has not yet reached a company bank account. It is not yours to spend and it is not an advance — it is money the company is waiting on.'}>
              <Info size={13} className="shrink-0 text-subtle" />
            </Tooltip>
          </div>
          <div className={`type-metric tabular-nums ${
            book === 'WALLET'
              ? 'text-content'
              : custodyBal > 0.005 ? 'text-warning-600 dark:text-harvest-300' : 'text-content'
          }`}>
            {inr2(book === 'WALLET' ? walletBal : custodyBal)}
          </div>
          <div className="type-caption text-subtle">
            {book === 'WALLET'
              ? 'yours to spend on approved expenses'
              : collectedTotal > 0.005
                /* The whole subtraction, so the figure can be checked rather than trusted. The expense
                   term only appears when there is one — most people never spend from collections. */
                ? `${inr2(collectedTotal)} collected − ${inr2(depositedTotal)} deposited${
                    spentOnExpenses > 0.005 ? ` − ${inr2(spentOnExpenses)} spent on approved expenses` : ''
                  }${movedToWallet > 0.005 ? ` − ${inr2(movedToWallet)} moved into your wallet` : ''}`
                : 'nothing collected yet'}
          </div>
          {/* The deposit HAS registered — it is just not yours to write off yet. Saying so here is what
              stops the rep recording it a second time because the headline figure did not move. */}
          {book === 'RETAILER_MONEY' && awaitingConfirmation > 0.005 && (
            <div className="mt-1 type-caption text-warning-600 dark:text-harvest-300">
              Includes {inr2(awaitingConfirmation)} you have banked, waiting for Accounts to confirm it against the account.
            </div>
          )}
        </div>

        {/* The action belongs beside the number it clears. */}
        {/* Only what is not already with the bank being checked — the server caps on the same figure. */}
        {book === 'RETAILER_MONEY' && canDeposit && custodyBal - awaitingConfirmation > 0.005 && (
          <Button variant="primary" onClick={() => setDepositOpen(true)}>Record a deposit</Button>
        )}
        {book === 'RETAILER_MONEY' && custodyBal <= 0.005 && collectedTotal > 0.005 && (
          <div className="type-caption text-subtle">Everything you collected has reached the company.</div>
        )}
      </div>

      <DataTable
        storageKey={book === 'WALLET' ? 'my-money-wallet' : 'my-money-retailer'}
        rowKey="_key"
        /* No fixed layout: with four columns of very different content, fixed widths on a wide screen
           pushed the amount to the far right and squeezed the description that carries the actual
           story. Natural sizing keeps them together and lets the description take the slack. */
        dataSource={rows}
        columns={cols}
        pagination={{ pageSize: 15 }}
        emptyText={<EmptyState title={book === 'WALLET' ? 'No wallet movements yet' : 'No retailer cash collected yet'} />}
      />

      {/*
        * WHOSE MONEY THIS DEPOSIT WAS, in full.
        *
        * The row can show two dealers before it runs out of width; everything past that used to live
        * only in a hover title, which is unreachable on a phone and impossible to read down. Here the
        * whole allocation is listed and totalled, so the rep can check a deposit against their own
        * collections line by line.
        */}
      <Modal
        open={!!coversFor}
        onOpenChange={(o) => { if (!o) setCoversFor(null); }}
        width={520}
        title={coversFor?.docNo ?? 'Deposit'}
        description={coversFor?.description ?? ''}
        footer={<Button variant="text" onClick={() => setCoversFor(null)}>Close</Button>}
      >
        {coversFor && (
          <div className="overflow-hidden rounded-card border border-line">
            <table className="w-full text-left">
              <tbody>
                {(coversFor.source?.covers ?? []).map((c: any, i: number) => (
                  <tr key={i} className="border-b border-line/60 last:border-0">
                    <td className="px-3 py-2 align-top">
                      <span className="block type-body text-content">{c.retailerName ?? 'Unknown retailer'}</span>
                      <span className="block font-mono type-caption text-subtle">
                        {[c.paymentNo, c.orderNo].filter(Boolean).join(' · ')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right align-top type-body font-medium tabular-nums text-content">
                      {inr2(c.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* It has to add up to the deposit, and saying so is what makes it checkable. */}
              <tfoot>
                <tr className="border-t border-line bg-surface-2/60">
                  <td className="px-3 py-2 type-label text-muted">Total deposited</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right type-body font-semibold tabular-nums text-content">
                    {inr2(coversFor.amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Modal>

      {depositOpen && (
        <RecordDepositModal outstanding={Math.max(0, Math.round((custodyBal - awaitingConfirmation) * 100) / 100)} onClose={() => setDepositOpen(false)} />
      )}
    </Card>
  );
}
